import _extends from '@babel/runtime/helpers/esm/extends';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React from 'react';
import clsx from 'clsx';
import { Icon, V2BankIcon, V2CausesIcon, V2MerchantIcon, V2ProfileIcon } from '@paypalcorp/pp-react-icons';
import { useTheme } from '@paypalcorp/pp-react-theme';
import { Shimmer } from '@paypalcorp/pp-react-utils';
import '../__generated__/styles/avatar.css';

var styles = {"ppvx--v2":"ppvx--v2___2-11-6","ppvx_avatar-stack":"ppvx_avatar-stack___2-11-6","ppvx--v1":"ppvx--v1___2-11-6","ppvx_avatar-stack__avatar":"ppvx_avatar-stack__avatar___2-11-6","ppvx_avatar-stack__screenreader":"ppvx_avatar-stack__screenreader___2-11-6","ppvx_avatar-stack__label":"ppvx_avatar-stack__label___2-11-6","ppvx_avatar":"ppvx_avatar___2-11-6","ppvx_icon":"ppvx_icon___2-11-6","ppvx_avatar__image":"ppvx_avatar__image___2-11-6","ppvx_avatar--size_xs":"ppvx_avatar--size_xs___2-11-6","ppvx_avatar--size_sm":"ppvx_avatar--size_sm___2-11-6","ppvx_avatar--size_md":"ppvx_avatar--size_md___2-11-6","ppvx_avatar--size_lg":"ppvx_avatar--size_lg___2-11-6","ppvx_avatar--color_grey":"ppvx_avatar--color_grey___2-11-6","ppvx_avatar--color_blue":"ppvx_avatar--color_blue___2-11-6","ppvx_avatar--color_green":"ppvx_avatar--color_green___2-11-6","ppvx_avatar--color_purple":"ppvx_avatar--color_purple___2-11-6","ppvx_avatar--color_pink":"ppvx_avatar--color_pink___2-11-6","ppvx_avatar--shimmer":"ppvx_avatar--shimmer___2-11-6","ppvx_avatar__icon":"ppvx_avatar__icon___2-11-6","ppvx_avatar--color_magenta":"ppvx_avatar--color_magenta___2-11-6","ppvx_avatar--color_yellow":"ppvx_avatar--color_yellow___2-11-6","ppvx_avatar--color_mint":"ppvx_avatar--color_mint___2-11-6","ppvx_avatar--color_lilac":"ppvx_avatar--color_lilac___2-11-6","ppvx_avatar--generic_profile":"ppvx_avatar--generic_profile___2-11-6","ppvx_avatar--generic_merchant":"ppvx_avatar--generic_merchant___2-11-6","ppvx_avatar--generic_causes":"ppvx_avatar--generic_causes___2-11-6","ppvx_avatar--generic_bank":"ppvx_avatar--generic_bank___2-11-6","ppvx_avatar__badge":"ppvx_avatar__badge___2-11-6"};

var _excluded = ["className", "size", "label", "isLabelHidden", "remainingCount", "children"];

var renderChildren = function (children, remainingCount, size) {
  var theme = 'v2';
  var avatarClass = clsx(styles['ppvx_avatar-stack__avatar']);
  var avatarComponents = [];
  React.Children.forEach(children, function (child, index) {
    return avatarComponents.push( /*#__PURE__*/React.createElement("div", {
      key: "avatarStacked__".concat(index),
      className: avatarClass
    }, React.cloneElement(child, {
      size: size,
      theme: theme
    })));
  });

  if (remainingCount) {
    var countToDisplay = remainingCount < 10 ? remainingCount : '9+';
    var key = children && children.length ? "avatarStacked__".concat(children.length) : 'avatarStacked';
    avatarComponents.push( /*#__PURE__*/React.createElement("div", {
      key: key,
      className: avatarClass
    }, /*#__PURE__*/React.createElement(Avatar, {
      theme: theme,
      size: size,
      initials: "".concat(countToDisplay)
    })));
  }

  return avatarComponents;
};

var AvatarStack = function (_ref) {
  var className = _ref.className,
      size = _ref.size,
      label = _ref.label,
      isLabelHidden = _ref.isLabelHidden,
      remainingCount = _ref.remainingCount,
      children = _ref.children,
      rest = _objectWithoutProperties(_ref, _excluded);

  var avatarStackClasses = clsx(styles["ppvx_avatar-stack"], styles["ppvx--v2"], className);
  var labelClass = clsx(styles["ppvx_avatar-stack__label"], isLabelHidden && styles["ppvx_avatar-stack__screenreader"]);
  return /*#__PURE__*/React.createElement("div", _extends({
    className: avatarStackClasses
  }, rest), renderChildren(children, remainingCount, size), /*#__PURE__*/React.createElement("span", {
    className: labelClass
  }, label));
};

var _excluded$1 = ["className", "color", "size", "src", "alt", "icon", "initials", "loading", "svgIconComponent", "badge", "generic", "theme", "isLoading", "isInverse", "children"];

var Avatar = function (_ref) {
  var _clsx;

  var className = _ref.className,
      color = _ref.color,
      size = _ref.size,
      src = _ref.src,
      alt = _ref.alt,
      icon = _ref.icon,
      initials = _ref.initials,
      loading = _ref.loading,
      svgIconComponent = _ref.svgIconComponent,
      badge = _ref.badge,
      generic = _ref.generic,
      theme = _ref.theme,
      isLoading = _ref.isLoading,
      isInverse = _ref.isInverse,
      children = _ref.children,
      rest = _objectWithoutProperties(_ref, _excluded$1);

  var _theme = useTheme('Avatar', theme);

  var isThemeV2 = _theme && _theme === 'v2';
  var v2InitalColors = ['magenta', 'green', 'purple', 'yellow'];
  var v2IconColors = ['blue', 'pink', 'mint', 'lilac'];
  var v2Sizes = ['lg', 'md', 'sm', 'xs'];

  if (_theme !== 'v2') {
    console.warn('Avatars 1.0 styles are deprecated. Use Avatars 2.0 styles and refer to our Migration Guide on how to use 2.0 styles/components.');
  }

  if (isThemeV2 && isLoading) {
    var cls = clsx(styles['ppvx_avatar--shimmer'], v2Sizes.includes(size) && styles["ppvx_avatar--size_".concat(size)], className);
    return /*#__PURE__*/React.createElement(Shimmer, {
      className: cls,
      isInverse: isInverse,
      hasStyleDefaults: false
    }, children);
  }

  var avatarClasses = clsx(styles.ppvx_avatar, (_clsx = {}, _defineProperty(_clsx, styles["ppvx_avatar--color_".concat(color)], color && color !== 'grey'), _defineProperty(_clsx, styles["ppvx_avatar--color_".concat(color)], isThemeV2 ? function v2ColorMapping(colour) {
    if (generic || src) return null;else if (initials) return v2InitalColors.includes(colour);
    return v2IconColors.includes(colour);
  }(color) : ['blue', 'pink', 'purple', 'green'].includes(color)), _defineProperty(_clsx, styles["ppvx_avatar--size_".concat(size)], isThemeV2 ? v2Sizes.includes(size) : ['md', 'sm', 'xs'].includes(size)), _defineProperty(_clsx, styles['ppvx--v2'], isThemeV2), _defineProperty(_clsx, styles["ppvx_avatar--generic_".concat(generic)], isThemeV2 && ['profile', 'merchant', 'causes', 'bank'].includes(generic)), _clsx), className);
  var svgClassName = clsx(_defineProperty({}, styles.ppvx_avatar__icon, isThemeV2));
  var iconSize = 'lg';

  if (isThemeV2) {
    iconSize = null;
  } else if (size === 'sm' || size === 'xs') {
    iconSize = 'xs';
  } else if (size === 'md') {
    iconSize = 'sm';
  }

  var avatarContent = function () {
    if (src) {
      return /*#__PURE__*/React.createElement("img", {
        className: styles.ppvx_avatar__image,
        src: src,
        alt: alt,
        loading: loading
      });
    }

    if (svgIconComponent || icon) {
      return /*#__PURE__*/React.createElement(svgIconComponent || Icon, {
        className: svgClassName,
        name: icon,
        size: iconSize
      });
    }

    if (generic) {
      var GenericIcon = {
        bank: V2BankIcon,
        causes: V2CausesIcon,
        merchant: V2MerchantIcon,
        profile: V2ProfileIcon
      }[generic];
      /* istanbul ignore next */

      if (GenericIcon) return /*#__PURE__*/React.createElement(GenericIcon, {
        className: svgClassName,
        name: generic,
        size: iconSize
      });
    }

    return initials;
  };

  var avatarBadge = badge && size !== 'xs' ? React.cloneElement(badge, {
    className: styles.ppvx_avatar__badge
  }) : null;
  return /*#__PURE__*/React.createElement("div", _extends({
    className: avatarClasses
  }, rest), avatarContent(), avatarBadge);
};

export { Avatar, AvatarStack };
